import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  LoadingMask,
  Paragraph,
  Text,
} from '@zillow/constellation-10';
import { useCreateRenterAccountWebApiHandler_createRenterAccount } from '@zg-rentals/ts-rent-payment-client-react-query';
import { logError } from '@zg-rentals/log-error';

import { formatDate } from '@common/paymentsUtils/dateUtils';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import { DEFAULT_ERROR } from '@renter-hub/constants/notifications';
import Page from '@renter-hub/components/RenterPage';
import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import MoveInCostsTypes from '@common/constants/MoveInCostsConstants';
import PontoonConstants from '@common/constants/PontoonExceptionConstants';
import OnboardingStatus from '@renter-hub/constants/onboardingStatus';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import REQUEST_STATUS from '@common/constants/requestStatus';
import { LandlordBlockCard } from '@renter-hub/modules/LandlordBlockCard/LandlordBlockCard.component';
import { detectCombinedNames } from '@common/utils/detectCombinedNames';
import errorMessages from '@common/constants/errorMessages';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import NotificationContainer from '@common/components/NotificationContainer';
import userPersonas from '@common/constants/userPersonas';

import type { RootState } from '../../../../store/types';
import type { MoveInCosts } from '@renter-hub/models/Types';

interface ConfirmRentDetailsPageProps {
  requestStatus: {
    rentDetails: string;
  };
  rentDetails: {
    name: string;
    firstName: string;
    email: string;
    address: {
      homeAddress: string;
      unit?: string;
      city: string;
      state: string;
      zipCode: string;
    };
    amount: string;
    firstDueDate: string;
    lastDueDate: string;
    monthToMonth: boolean;
    moveInCosts?: MoveInCosts;
    thumbnail?: any;
    renterFirstName: string;
    renterLastName: string;
  };
  requestRentDetails: () => any;
  onCreateRenterAccountSuccess: (featureFlagEnableRentReporting: boolean) => any;
}

const ConfirmRentDetailsPage = (props: ConfirmRentDetailsPageProps) => {
  const dispatch = useDispatch();

  const { featureFlagEnableRentReporting, user, onboardingStatus } = useSelector((state: RootState) => ({
    featureFlagEnableRentReporting: state.payments.renter.dashboard.enableCreditReporting,
    user: state.user,
    onboardingStatus: state.payments.renter.dashboard.onboardingStatus,
  }));

  const searchString = useLocation().search;
  const queryParams = queryString.parse(searchString);
  const inviteToken = (queryParams.invite as string) || '';

  const [updatedRenterFirstName, setUpdatedRenterFirstName] = useState({ value: '', error: '' });
  const [updatedRenterLastName, setUpdatedRenterLastName] = useState({ value: '', error: '' });
  const [isLoading, setIsLoading] = useState(true);

  const { mutate: createRenterAccount } = useCreateRenterAccountWebApiHandler_createRenterAccount({
    onMutate: () => {
      dispatch(closeNotification());
    },
    onSuccess: () => {
      props.onCreateRenterAccountSuccess(featureFlagEnableRentReporting);
    },
    onError: (error) => {
      dispatch(showPageNotification(DEFAULT_ERROR));
      logError({ error, context: { details: 'onCreateRenterAccountError - confirmRentDetailsPage' } });
    },
  });

  useEffect(() => {
    Promise.resolve()
      .then(() => props.requestRentDetails())
      .then((response) => {
        setUpdatedRenterFirstName({
          value: response.data.renterFirstName,
          error: detectCombinedNames(response.data.renterFirstName)
            ? errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM
            : '',
        });
        setUpdatedRenterLastName({
          value: response.data.renterLastName,
          error: detectCombinedNames(response.data.renterLastName)
            ? errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM
            : '',
        });
      })
      .finally(() => setIsLoading(false));
  }, []);

  const onSubmit = () => {
    const updatedFirstName = updatedRenterFirstName.value;
    const updatedLastName = updatedRenterLastName.value;
    const existingFirstName = props.rentDetails.renterFirstName;
    const existingLastName = props.rentDetails.renterLastName;

    RachelCSEvents.emitClickConfirmDetailsEvent({
      user,
    });

    createRenterAccount({
      postData: {
        encryptedInvite: inviteToken,
        acceptTerms: true,
        firstName: updatedFirstName != existingFirstName ? updatedFirstName : existingFirstName,
        lastName: updatedLastName != existingLastName ? updatedLastName : existingLastName,
      },
    });
  };

  const onEditFirstName = (e: React.ChangeEvent<HTMLInputElement>) => {
    let error = '';
    if (detectCombinedNames(e.currentTarget.value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM;
    }

    setUpdatedRenterFirstName({
      value: e.currentTarget.value,
      error,
    });
  };

  const onEditLastName = (e: React.ChangeEvent<HTMLInputElement>) => {
    let error = '';
    if (detectCombinedNames(e.currentTarget.value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM;
    }

    setUpdatedRenterLastName({
      value: e.currentTarget.value,
      error,
    });
  };

  const renderMoveInCosts = (moveInCosts: MoveInCosts) => {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'default',
        }}
      >
        <Box>
          <Text textStyle="body-bold">Move-in payment: </Text>
          <Text className="fs-exclude">
            Due on{' '}
            {formatDate(
              moveInCosts.dueDate,
              'MMMM d, yyyy',
              PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.MOVE_IN_COSTS.DUE_DATE,
            )}
          </Text>
        </Box>
        {moveInCosts.moveInCostsItems.map((moveInCostItem, i) => (
          <Box
            key={`moveInCosts${i}`}
            css={{
              display: 'grid',
              gridTemplateColumns: '9fr 3fr',
              marginBottom: 'tighter',
            }}
          >
            <Box>
              <Paragraph textStyle="body-sm">
                {moveInCostItem.subType || MoveInCostsTypes[moveInCostItem.type]}
              </Paragraph>
              <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                {moveInCostItem.notes}
              </Text>
            </Box>
            <Box css={{ textAlign: 'right' }}>
              <Text textStyle="body-sm">{centsToDollars(moveInCostItem.amount)}</Text>
            </Box>
          </Box>
        ))}
        <Divider />
        <Box css={{ display: 'grid', gridTemplateColumns: '9fr 3fr' }} className="fs-exclude">
          <Paragraph textStyle="body-bold">Total payment</Paragraph>
          <Box css={{ textAlign: 'right' }}>
            <Text textStyle="body-bold">{centsToDollars(moveInCosts.amount)}</Text>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderBody = () => {
    const { rentDetails } = props;
    const { homeAddress, unit, city, state } = rentDetails.address;
    const formattedAddress = `${homeAddress} ${unit ? `${unit} ` : ''}${city}, ${state}`;
    const requestStatus = props.requestStatus.rentDetails;
    const { moveInCosts } = rentDetails;
    if (onboardingStatus === OnboardingStatus.REJECTED) {
      return <LandlordBlockCard isDashboard={false} />;
    } else if (requestStatus === REQUEST_STATUS.SUCCESS) {
      return (
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'default' }}>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'loose',
              gridColumnEnd: { base: 'span 12', md: 'span 8' },
            }}
          >
            <Box>
              <Heading level={1} textStyle="heading-xs" css={{ marginBottom: 'tighter' }}>
                Confirm payment details
              </Heading>
              <Paragraph css={{ marginBottom: 'loose' }}>
                These are the details that {rentDetails.name} set up. If anything&apos;s incorrect, contact them to
                request an update.
              </Paragraph>
              <Box css={{ display: 'grid', flexDirection: 'column', gap: 'loose' }}>
                <Card css={{ padding: 'loose' }} elevated={false}>
                  <Box css={{ marginBottom: 'loose' }}>
                    <Text textStyle="body-bold">Your rental: </Text>
                    <Text>{formattedAddress}</Text>
                  </Box>
                  <Box
                    css={{
                      display: 'grid',
                      gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
                      gap: 'loose',
                    }}
                  >
                    <Box>
                      <Paragraph>Monthly rent</Paragraph>
                      <Paragraph textStyle="body-bold">{centsToDollars(parseInt(rentDetails.amount, 10))}</Paragraph>
                    </Box>
                    <Box>
                      <Paragraph>Due date</Paragraph>
                      <Paragraph textStyle="body-bold">
                        {formatDate(
                          rentDetails.firstDueDate,
                          'do',
                          PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.RENT_PAYMENT.FIRST_DUE_DATE,
                        )}{' '}
                        of the month
                      </Paragraph>
                    </Box>
                    <Box>
                      <Paragraph>Pay to</Paragraph>
                      <Paragraph textStyle="body-bold">{rentDetails.name}</Paragraph>
                    </Box>
                    <Box>
                      <Paragraph>Payment period</Paragraph>
                      <Paragraph textStyle="body-bold">
                        {formatDate(
                          rentDetails.firstDueDate,
                          'MMMM d, yyyy',
                          PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.RENT_PAYMENT.FIRST_DUE_DATE,
                        )}
                        &nbsp;
                        {rentDetails.monthToMonth
                          ? '- No end date/Month-to-month'
                          : `to ${formatDate(
                              rentDetails.lastDueDate,
                              'MMMM d, yyyy',
                              PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.RENT_PAYMENT.LAST_DUE_DATE,
                            )}`}
                      </Paragraph>
                    </Box>
                  </Box>
                </Card>

                {moveInCosts && moveInCosts.moveInCostsItems && moveInCosts.moveInCostsItems.length > 0 && (
                  <Card css={{ padding: 'loose' }} elevated={false}>
                    {renderMoveInCosts(moveInCosts)}
                  </Card>
                )}

                <Card css={{ padding: 'loose' }} elevated={false}>
                  <Paragraph textStyle="body-bold" css={{ marginBottom: 'default' }}>
                    Verify your name and correct any errors
                  </Paragraph>
                  <Box
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 'default',
                      justifyContent: 'space-between',
                      lg: {
                        flexDirection: 'row',
                      },
                    }}
                  >
                    <FormField
                      css={{ flexBasis: '50%', width: '100%' }}
                      control={
                        <Input
                          css={{ padding: '10px 16px 10px 16px' }}
                          onChange={onEditFirstName}
                          value={updatedRenterFirstName.value}
                        />
                      }
                      error={!!updatedRenterFirstName.error}
                      label={<Label>First name</Label>}
                      description={
                        updatedRenterFirstName.error ? (
                          <FormHelp css={{ marginTop: '4px' }} error>
                            {updatedRenterFirstName.error}
                          </FormHelp>
                        ) : null
                      }
                    />
                    <FormField
                      css={{ flexBasis: '50%', width: '100%' }}
                      control={
                        <Input
                          css={{ padding: '10px 16px 10px 16px' }}
                          onChange={onEditLastName}
                          value={updatedRenterLastName.value}
                        />
                      }
                      error={!!updatedRenterLastName.error}
                      label={<Label>Last name</Label>}
                      description={
                        updatedRenterLastName.error ? (
                          <FormHelp css={{ marginTop: '4px' }} error>
                            {updatedRenterLastName.error}
                          </FormHelp>
                        ) : null
                      }
                    />
                  </Box>
                </Card>
                <Box>
                  <ButtonGroup aria-label="confirm actions" css={{ marginBottom: 'tighter' }}>
                    <Button emphasis="filled" onClick={onSubmit}>
                      Confirm details
                    </Button>
                    <Button asChild>
                      <a href={`mailto:${rentDetails.email}`}>Contact {rentDetails.firstName || ''}</a>
                    </Button>
                  </ButtonGroup>
                  <Text css={{ color: 'text.subtle' }} textStyle="fineprint">
                    By clicking <Text textStyle="fineprint-bold">Confirm details</Text>, you agree to comply with
                    the&nbsp;
                    <Anchor target="_blank" href="https://www.zillow.com/corp/Terms.htm">
                      Terms of Use
                    </Anchor>
                    ,&nbsp;
                    <Anchor target="_blank" href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms">
                      Rental User Terms
                    </Anchor>
                    , and our third-party payment processor&apos;s&nbsp;
                    <Anchor target="_blank" href="https://stripe.com/us/privacy">
                      Payment Terms&nbsp;
                    </Anchor>
                    and&nbsp;
                    <Anchor target="_blank" href="https://plaid.com/legal">
                      Bank Authorization Terms.
                    </Anchor>
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    } else if (requestStatus === REQUEST_STATUS.FAIL) {
      return (
        <div className="pal">
          <Text>
            Could not load page.&nbsp;
            <Anchor type="primary" href="/renter-hub">
              Return to renter hub
            </Anchor>
          </Text>
        </div>
      );
    }
  };

  return (
    <Page.MainSection>
      <HelmetContainer {...SeoConstants.confirmRentDetails} />
      <NotificationContainer persona={userPersonas.RENTER} />
      <LoadingMask loading={isLoading} loadingVoiceOver="Loading" role="status">
        {renderBody()}
      </LoadingMask>
    </Page.MainSection>
  );
};

export default ConfirmRentDetailsPage;
